import api from "services/api";

import {
  IAlcanceDoVinculo,
  IBalancoDaPessoa,
  IClienteDaCarteira,
  IConvitePendente,
  IEuNaGestao,
  IEventoDeAcesso,
  IResultadoDoConvite,
  IUsuarioDaOrganizacao,
  PedidoDeAlcance,
} from "./gestao-de-usuarios.types";

// why: F3 (plano 11 T7). As recusas do backend (403, 409) trazem `code` e a frase do §10 da
// spec; a tela mostra a frase tal qual, sem inventar outra.
export class ErroDeAcesso extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function chamar<T>(pedido: Promise<{ data: { data: T } }>): Promise<T> {
  try {
    return (await pedido).data?.data as T;
  } catch (erro) {
    const resposta = (
      erro as { response?: { status?: number; data?: { data?: { code?: string; message?: string } } } }
    )?.response;
    const corpo = resposta?.data?.data;
    // why: 500 traz o texto cru do erro (banco, TypeError); só recusa (4xx) tem frase para a tela.
    if (corpo?.message && (resposta?.status ?? 500) < 500) throw new ErroDeAcesso(corpo.code ?? "ERRO", corpo.message);
    throw erro;
  }
}

const BASE = "/v2/acesso";

export const gestaoDeUsuarios = {
  eu: () => chamar<IEuNaGestao>(api.get(`${BASE}/eu`)),
  usuarios: (organizationId: string) =>
    chamar<IUsuarioDaOrganizacao[]>(api.get(`${BASE}/organizacoes/${organizationId}/usuarios`)),
  carteira: () => chamar<IClienteDaCarteira[]>(api.get(`${BASE}/carteira`)),
  // why: cursor composto; a revisão grava muitos eventos no mesmo instante (plano 11 §7c).
  eventos: (organizationId: string, cursor?: { antesDe: string; antesDeId: string }) =>
    chamar<IEventoDeAcesso[]>(api.get(`${BASE}/organizacoes/${organizationId}/eventos`, { params: cursor })),
  // why: sem `alcance` o corpo não leva a chave (o backend aplica o padrão de hoje).
  convidar: (
    organizationId: string,
    corpo: { nome: string; email: string; papeis: string[]; alcance?: PedidoDeAlcance },
  ) =>
    chamar<IResultadoDoConvite>(api.post(`${BASE}/organizacoes/${organizationId}/convites`, corpo)),
  convites: (organizationId: string) =>
    chamar<IConvitePendente[]>(api.get(`${BASE}/organizacoes/${organizationId}/convites`)),
  reenviarConvite: (organizationId: string, conviteId: string) =>
    chamar<void>(api.post(`${BASE}/organizacoes/${organizationId}/convites/${conviteId}/reenvio`)),
  mudarPapeisDoConvite: (organizationId: string, conviteId: string, papeis: string[]) =>
    chamar<void>(api.put(`${BASE}/organizacoes/${organizationId}/convites/${conviteId}/papeis`, { papeis })),
  mudarAlcanceDoConvite: (organizationId: string, conviteId: string, pedido: PedidoDeAlcance) =>
    chamar<void>(api.put(`${BASE}/organizacoes/${organizationId}/convites/${conviteId}/alcance`, pedido)),
  cancelarConvite: (organizationId: string, conviteId: string) =>
    chamar<void>(api.delete(`${BASE}/organizacoes/${organizationId}/convites/${conviteId}`)),
  adicionarPapel: (organizationUserId: string, papel: string) =>
    chamar<void>(api.post(`${BASE}/vinculos/${organizationUserId}/papeis`, { papel })),
  removerPapel: (organizationUserId: string, papel: string) =>
    chamar<void>(api.delete(`${BASE}/vinculos/${organizationUserId}/papeis/${papel}`)),
  remover: (organizationUserId: string) => chamar<void>(api.delete(`${BASE}/vinculos/${organizationUserId}`)),
  alcance: (organizationUserId: string) =>
    chamar<IAlcanceDoVinculo>(api.get(`${BASE}/vinculos/${organizationUserId}/alcance`)),
  definirAlcance: (organizationUserId: string, pedido: PedidoDeAlcance) =>
    chamar<void>(api.put(`${BASE}/vinculos/${organizationUserId}/alcance`, pedido)),
  decidirEmpresa: (organizationUserId: string, organizationId: string, incluir: boolean) =>
    chamar<void>(api.put(`${BASE}/vinculos/${organizationUserId}/selecao/${organizationId}`, { incluir })),
  balancoDoVinculo: (organizationUserId: string) =>
    chamar<IBalancoDaPessoa>(api.get(`${BASE}/vinculos/${organizationUserId}/balanco`)),
  mudarBalancoDoVinculo: (organizationUserId: string, recebe: boolean) =>
    chamar<void>(api.put(`${BASE}/vinculos/${organizationUserId}/balanco`, { recebe })),
  meuBalanco: () => chamar<IBalancoDaPessoa>(api.get(`${BASE}/eu/balanco`)),
  mudarMeuBalanco: (recebe: boolean) => chamar<void>(api.put(`${BASE}/eu/balanco`, { recebe })),
  suspender: (userId: string) => chamar<void>(api.post(`${BASE}/usuarios/${userId}/suspensao`)),
  reativar: (userId: string) => chamar<void>(api.delete(`${BASE}/usuarios/${userId}/suspensao`)),
  revisar: (organizationId: string, gestores: string[]) =>
    chamar<void>(api.post(`${BASE}/organizacoes/${organizationId}/revisao`, { gestores })),
};
